// Facts shown on the public site (landing, privacy, terms). Edit here, not in the pages.

export const SITE = {
  product: 'YADA',
  company: 'Fluxibit Solutions',
  city: 'Addis Ababa, Ethiopia',
  email: 'hello@yadahub.cloud',
  domain: 'yadahub.cloud',
  playUrl: 'https://play.google.com/store/apps/details?id=com.fluxibit.yada',
  policyUpdated: '15 October 2026',
};

export type TeamMember = { name: string; role: string; bio: string; linkedin?: string };

export const TEAM: TeamMember[] = [
  {
    name: 'Dr. Alazar Alemayehu',
    role: 'Co-founder & CEO',
    bio: 'Medical doctor turned builder. Leads product, engineering and the AI that powers YADA.',
    linkedin: 'https://www.linkedin.com/in/alazar-alemayehu/',
  },
  {
    name: 'Matiwos Tariku',
    role: 'Co-founder & COO',
    bio: 'Runs operations, finance and partnerships with schools and education bureaus.',
    linkedin: 'https://www.linkedin.com/in/matiwos-tariku-md-9063422a0/',
  },
];

export const mailto = (subject?: string) =>
  `mailto:${SITE.email}${subject ? `?subject=${encodeURIComponent(subject)}` : ''}`;

// Videos on the YADA YouTube channel (@YADA-fluxibit).
export type Video = { id: string; title: string; summary: string; duration: string };

export const CHANNEL_URL = 'https://www.youtube.com/@YADA-fluxibit';

export const AD_VIDEO: Video = {
  id: '2fEx_CUtcGU',
  title: 'YADA for Schools: see every student, act before the national exam',
  summary: 'Early-warning risk scores, AI exams from your own textbooks, paper sheets marked by phone and weekly updates for families.',
  duration: '2:30',
};

export const SCHOOL_TUTORIALS_PLAYLIST = 'https://www.youtube.com/playlist?list=PLerEUm-Ml6W4';

export type TutorialStage = { title: string; videos: Video[] };

export const SCHOOL_TUTORIALS: TutorialStage[] = [
  { title: 'Set up your school', videos: [
    { id: 'WP9nCHkwtQ4', title: 'Getting started', duration: '2:06',
      summary: 'Sign in for the first time, choose your password, and meet the setup checklist.' },
    { id: 'y-uQOYTPLWw', title: 'Set up grades, subjects and chapters', duration: '1:42',
      summary: 'Build your school’s structure once. Every exam, report and alert uses it.' },
    { id: 'Qex2keBkWAM', title: 'Add staff, teachers and sections', duration: '2:03',
      summary: 'Create accounts, share sign-in details, assign classes, and split grades into sections.' },
    { id: '7NhzrRTw-K8', title: 'Bring your students in', duration: '2:24',
      summary: 'Approve join requests, place students in sections, and find anyone in seconds.' },
  ] },
  { title: 'Know every student', videos: [
    { id: 'dKsb8h_e3BA', title: 'Student 360: know every student', duration: '3:57',
      summary: 'One page with a student’s results, habits, wellbeing, future plans and family.' },
    { id: 'fX4FZ2hNW9I', title: 'Alerts and cases', duration: '1:45',
      summary: 'YADA watches every student overnight. Here’s how your staff respond.' },
  ] },
  { title: 'Teach and assess', videos: [
    { id: 'jLF-nX7855w', title: 'Create exams and read reports', duration: '4:47',
      summary: 'Write an exam yourself or with AI, publish it, then see exactly what students missed.' },
    { id: 'w4Rhdaa2n3g', title: 'Worksheets, homework and marking', duration: '2:19',
      summary: 'More question types, attachments, and AI help with marking written answers.' },
    { id: 'b9SMnKWpmzk', title: 'Paper exams', duration: '3:04',
      summary: 'No phones in class? Print YADA answer sheets, scan them, and let YADA mark them.' },
  ] },
  { title: 'Run the school', videos: [
    { id: 'QVvV5Y_vOwU', title: 'Class performance', duration: '1:52',
      summary: 'See how every grade, section and subject is doing, and act on weak chapters.' },
    { id: 'yCYkjfXTKA4', title: 'Tasks, notices and the parent digest', duration: '1:40',
      summary: 'Track assigned work, send notices, nudge late students, and keep families informed.' },
    { id: 'unGQe2jq0_E', title: 'The YADAHub app for teachers', duration: '2:19',
      summary: 'Everything a teacher needs, on an Android phone.' },
    { id: '05CyrtB4vuA', title: 'The AI coach', duration: '3:07',
      summary: 'Ask about your school, a class or one student in plain language, and get answers from your own data.' },
  ] },
];

/** All school tutorials in order, numbered from 1. */
export const SCHOOL_TUTORIAL_LIST = SCHOOL_TUTORIALS.flatMap((s) => s.videos.map((v) => ({ ...v, stage: s.title })))
  .map((v, i) => ({ ...v, n: i + 1 }));

/** "m:ss" durations added up, in whole minutes. */
export const totalMinutes = (videos: Video[]) =>
  Math.round(videos.reduce((sum, v) => { const [m, s] = v.duration.split(':').map(Number); return sum + m * 60 + s; }, 0) / 60);
